// ./custom-widgets/Draw.ts
import MapView from "@arcgis/core/views/MapView";
import Draw from "@arcgis/core/views/draw/Draw";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Graphic from "@arcgis/core/Graphic";
import Polygon from "@arcgis/core/geometry/Polygon";
import Polyline from "@arcgis/core/geometry/Polyline";
import Point from "@arcgis/core/geometry/Point";
import Extent from "@arcgis/core/geometry/Extent";
import Circle from "@arcgis/core/geometry/Circle";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import TextSymbol from "@arcgis/core/symbols/TextSymbol";
import Color from "@arcgis/core/Color";

type DrawType = "point" | "polyline" | "polygon" | "rectangle" | "circle" | "ellipse" | "text";
type DrawMode = "click" | "freehand" | "hybrid";
type ToolFamily = "point" | "line" | "polygon" | "text";

type LineStyle =
    "solid" | "dash" | "dot" | "dash-dot" | "long-dash" | "long-dash-dot" | "none";
type Handle = { remove: () => void };
let actionHandles: Handle[] = [];
let selectedBtn: HTMLButtonElement | null = null;
type Op = Graphic[];
let doneStack: Op[] = [];
let redoStack: Op[] = [];
let textValue = "";
let textColorHex = "#000000";
let textSize = 16;
let textHaloHex = "#ffffff";
let textHaloSize = 1;

export interface DrawWidgetOptions {
    graphicsLayer?: GraphicsLayer;
    stroke?: string | number[];
    fill?: string | number[];
    onComplete?: (g: Graphic) => void;
}

export interface DrawWidgetHandle {
    element: HTMLElement;
    getLayer(): GraphicsLayer;
    clear(): void;
    destroy(): void;
    setVisible(v: boolean): void;
    start(type: DrawType, mode?: DrawMode): void;
}

export function drawWidget(view: MapView, opts: DrawWidgetOptions = {}): DrawWidgetHandle {
    const layer = opts.graphicsLayer ?? new GraphicsLayer({ listMode: "hide" });
    if (!opts.graphicsLayer) view.map?.add(layer);

    const sketchLayer = new GraphicsLayer({ listMode: "hide" });
    view.map?.add(sketchLayer);

    const draw = new Draw({ view });

    const root = document.createElement("div");
    root.className = "wab-draw exb-pop";
    const isMobile = window.innerWidth <= 768;
    root.style.width = isMobile ? "min(90vw, 320px)" : "fit-content";
    root.style.maxWidth = isMobile ? "90vw" : "none";

    const css = document.createElement("style");
    css.textContent = `
      .wab-draw { font-family: var(--esri-widget-font-family, Arial, sans-serif); }

      .wab-draw .hdr{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px !important;}

      .wab-draw .grid{
        display: flex;
        flex-wrap: wrap;
        gap: 8px !important;
        margin: 8px 0 16px !important;
      }
      .wab-draw .btn{ width:20px; height:28px; border:1px solid #dcdcdc;border-radius:4px;background:#fff;cursor:pointer;display:grid;place-items:center; }
      .wab-draw .btn[aria-pressed="true"]{ outline:2px solid #7ab7db;background:#e6f2fb; }
      .wab-draw .btn:disabled{ opacity:.5;cursor:default; }

      .wab-draw .preview-box{
        height:50px;border-radius:2px;background:#f3f3f3;display:flex;align-items:center;justify-content:center;
        margin: 6px 0 14px !important;
      }
      .wab-draw .preview-svg{ width:100%;height:100%; }

      .wab-draw .section-title{ display:block;font-weight:600;margin: 6px 0 8px !important; }

      .wab-draw .gallery{
        display:grid;grid-template-columns:repeat(6,28px);
        gap: 28px !important;
        margin: 8px 0 16px !important;
      }
      .wab-draw .gallery .sw{ width:28px;height:28px;border-radius:4px;border:1px solid #ddd;display:grid;place-items:center;cursor:pointer;background:#fff; }
      .wab-draw .gallery .sw[aria-selected="true"]{ outline:2px solid #7ab7db; }

      .wab-draw .row{ display:flex;gap:10px !important;align-items:center;margin:12px 0 !important; }
      .wab-draw label{ font-size:12px;color:#4a4a4a;min-width:120px; }

      .wab-draw .fill,.wab-draw .stroke,.wab-draw .outline{ width:100%;height:28px;border:1px solid #dcdcdc;border-radius:4px;padding:0;cursor:pointer; }
      .wab-draw select,.wab-draw input[type="number"]{ height:28px;border:1px solid #dcdcdc;border-radius:4px;padding:0 6px;width:100%; }
      .wab-draw input[type="range"]{ width:100%; }

      .wab-draw .chk{ display:flex; align-items:center; gap:8px; margin: 12px 0 !important; }
      .wab-draw .footer{ display:flex; justify-content: center; gap: 10px !important; margin-top: 14px !important; }

      .wab-draw .small{ font-size:12px;color:#666; }

      @media (max-width: 768px) {
        .wab-draw { padding: 6px; }
        .wab-draw .hdr { flex-direction: column; align-items: flex-start; gap: 1px; margin-bottom: 4px !important; }
        .wab-draw .title { font-size: 13px; font-weight: 600; }
        .wab-draw .subtle { font-size: 11px; color: #666; }
        .wab-draw .grid { gap: 4px !important; margin: 6px 0 10px !important; }
        .wab-draw .btn { width: 26px; height: 26px; font-size: 11px; }
        .wab-draw .section-title { font-size: 12px; margin: 4px 0 6px !important; }
        .wab-draw .preview-box { height: 40px; margin: 4px 0 10px !important; }
        .wab-draw .gallery { grid-template-columns: repeat(6, 24px); gap: 4px !important; margin: 6px 0 10px !important; }
        .wab-draw .gallery .sw { width: 24px; height: 24px; }
        .wab-draw .row { gap: 6px !important; margin: 8px 0 !important; flex-wrap: wrap; }
        .wab-draw label { font-size: 11px; min-width: 80px; }
        .wab-draw .fill, .wab-draw .stroke, .wab-draw .outline { height: 24px; }
        .wab-draw select, .wab-draw input[type="number"] { height: 24px; font-size: 12px; padding: 0 4px; }
        .wab-draw .chk { gap: 6px; margin: 8px 0 !important; font-size: 11px; }
        .wab-draw .footer { gap: 6px !important; margin-top: 10px !important; }
        .wab-draw .footer button { font-size: 12px; padding: 6px 10px; }
        .wab-draw .small { font-size: 10px; }
      }
    `;
    root.appendChild(css);

    const header = document.createElement("div");
    header.className = "hdr";
    header.innerHTML = `<div class="title">Draw</div><div class="subtle">Select draw mode</div>`;
    root.appendChild(header);

    const grid = document.createElement("div");
    grid.className = "grid";
    grid.innerHTML = `
      <button class="btn" data-tool="point" title="Point">●</button>
      <button class="btn" data-tool="polyline" data-mode="click" title="Line">⎯</button>
      <button class="btn" data-tool="polyline" data-mode="freehand" title="Freehand Line">∿</button>
      <button class="btn" data-tool="polygon" data-mode="click" title="Polygon">▱</button>
      <button class="btn" data-tool="rectangle" data-mode="click" title="Rectangle">▭</button>
      <button class="btn" data-tool="circle" data-mode="click" title="Circle">◯</button>
      <button class="btn" data-tool="ellipse" data-mode="click" title="Ellipse">⬭</button>
      <button class="btn" data-tool="text" title="Text">A</button>
    `;

    root.appendChild(grid);

    // Preview
    const prevLabel = document.createElement("div");
    prevLabel.className = "section-title";
    prevLabel.textContent = "Preview:";

    const previewBox = document.createElement("div");
    previewBox.className = "preview-box";
    const previewSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    previewSvg.classList.add("preview-svg");
    previewSvg.setAttribute("viewBox", "0 0 100 50");
    previewBox.appendChild(previewSvg);

    function showPanel(v: boolean) {
        prevLabel.style.display = v ? "block" : "none";
        previewBox.style.display = v ? "block" : "none";
        controlsHost.style.display = v ? "block" : "none";
        rowMeas.style.display = v ? "flex" : "none";
        footer.style.display = v ? "flex" : "none";
    }

    root.appendChild(prevLabel);
    root.appendChild(previewBox);

    // Host for per-family controls
    const controlsHost = document.createElement("div");
    root.appendChild(controlsHost);

    // Common footer
    const rowMeas = document.createElement("div"); rowMeas.className = "chk";
    rowMeas.innerHTML = `<input id="measChk" type="checkbox"/><label for="measChk" class="small">Show Measurements</label>`;
    root.appendChild(rowMeas);

    const footer = document.createElement("div"); footer.className = "footer";
    footer.innerHTML = `
        <button class="btn" id="undoBtn" title="Undo" aria-label="Undo">
          <calcite-icon icon="undo" scale="s"></calcite-icon>
        </button>
        <button class="btn" id="redoBtn" title="Redo" aria-label="Redo">
          <calcite-icon icon="redo" scale="s"></calcite-icon>
        </button>
        <button class="btn" id="clearBtn" title="Clear" aria-label="Clear">
          <calcite-icon icon="trash" scale="s"></calcite-icon>
        </button>
    `;
    root.appendChild(footer);

    showPanel(false);

    // --- state ---
    let family: ToolFamily = "line";
    let activeType: DrawType = "polyline";
    let activeMode: DrawMode | undefined;

    // line state
    let strokeHex = toHex(opts.stroke ?? [0, 121, 193, 1]);
    let strokeWidth = 3;
    let strokeStyle: LineStyle = "solid";
    let strokeTransparencyPct = 0;

    // polygon state
    let fillHex = toHex(opts.fill ?? [0, 121, 193, 0.2]);
    let outlineHex = strokeHex;
    let outlineWidth = 2;
    let outlineStyle: LineStyle = "solid";
    let fillTransparencyPct = 0;
    let noFill = false;

    // point state
    let pointFillHex = "#2b6cb0";
    let pointOutlineHex = "#1a365d";
    let pointOutlineWidth = 1;
    let pointSize = 16;
    let pointTransparencyPct = 0;

    const measChk = rowMeas.querySelector<HTMLInputElement>("#measChk")!;

    let activeAction: __esri.DrawAction | null = null;
    let preview: Graphic | null = null;

    // ArcGIS enum mapping for line styles
    const STYLE_MAP: Record<LineStyle, __esri.SimpleLineSymbol["style"]> = {
        solid: "solid",
        dash: "dash",
        dot: "short-dot",
        "dash-dot": "short-dash-dot",
        "long-dash": "long-dash",
        "long-dash-dot": "long-dash-dot",
        none: "none",
    };

    // ======== RENDER CONTROLS PER FAMILY ========
    function renderControls() {
        controlsHost.innerHTML = "";
        previewSvg.innerHTML = "";

        if (family === "line") {
            // --- preview: line ---
            const line = document.createElementNS("http://www.w3.org/2000/svg", "line");
            line.setAttribute("x1", "10"); line.setAttribute("y1", "25");
            line.setAttribute("x2", "90"); line.setAttribute("y2", "25");
            previewSvg.appendChild(line);

            // Basic swatches (shared palette)
            const galLabel = document.createElement("div");
            galLabel.className = "section-title";
            galLabel.textContent = "Basic";
            const gal = document.createElement("div"); gal.className = "gallery";
            const palette = ["#f87171", "#fb923c", "#facc15", "#4ade80", "#60a5fa", "#a78bfa", "#f472b6", "#94a3b8", "#fbbf24", "#34d399", "#22d3ee", "#fda4af"];

            // keep a reference to the <input type="color"> so we can sync it
            const strokeColorInput = colorInput(strokeHex, v => { strokeHex = v; updateLinePreview(line); });
            const r1 = row("Color:", strokeColorInput);

            let selIndex = Math.max(0, palette.indexOf(strokeHex)); // highlight if current color is in palette
            palette.forEach((hex, i) => {
                const sw = document.createElement("button");
                sw.type = "button";
                sw.className = "sw";
                sw.setAttribute("aria-selected", String(i === selIndex));
                sw.style.background = hex;
                sw.onclick = () => {
                    selIndex = i;
                    strokeHex = hex;
                    strokeColorInput.value = hex;
                    updateLinePreview(line);

                    gal.querySelectorAll(".sw").forEach(n => n.setAttribute("aria-selected", "false"));
                    sw.setAttribute("aria-selected", "true");
                };
                gal.appendChild(sw);
            });

            // other rows
            const r2 = row("Style:", styleSelect(strokeStyle, v => { strokeStyle = v; updateLinePreview(line); }));
            const r3 = row("Transparency:", range(strokeTransparencyPct, v => { strokeTransparencyPct = v; updateLinePreview(line); }, "%"));
            const r4 = row("Width:", number(strokeWidth, 1, 24, 1, v => { strokeWidth = v; updateLinePreview(line); }));

            // append controls
            controlsHost.append(galLabel, gal, r1, r2, r3, r4);
            updateLinePreview(line);
        }


        if (family === "polygon") {
            const tri = document.createElementNS("http://www.w3.org/2000/svg", "polygon");
            tri.setAttribute("points", "30,35 50,10 70,35");
            previewSvg.appendChild(tri);

            const galLabel = document.createElement("div");
            galLabel.className = "section-title";
            galLabel.textContent = "Basic";
            const gal = document.createElement("div"); gal.className = "gallery";
            const palette = ["#f87171", "#fb923c", "#facc15", "#4ade80", "#60a5fa", "#a78bfa", "#f472b6", "#94a3b8", "#fbbf24", "#34d399", "#22d3ee", "#fda4af"];
            let selIndex = 4;
            palette.forEach((hex, i) => {
                const sw = document.createElement("button");
                sw.type = "button";
                sw.className = "sw";
                sw.setAttribute("aria-selected", String(i === selIndex));
                sw.style.background = hex;
                sw.onclick = () => {
                    selIndex = i;
                    fillHex = hex;
                    fillColorInput.value = hex;
                    updatePolygonPreview(tri);
                    gal.querySelectorAll(".sw").forEach(n => n.setAttribute("aria-selected", "false"));
                    sw.setAttribute("aria-selected", "true");
                };
                gal.appendChild(sw);
            });

            // controls
            const fillColorInput = colorInput(fillHex, v => {
                fillHex = v;
                updatePolygonPreview(tri);
            });
            const r1 = row("Color:", fillColorInput);

            const fillRange = range(fillTransparencyPct, v => { fillTransparencyPct = v; updatePolygonPreview(tri); }, "%");
            const r2 = row("Transparency:", fillRange);

            const r3 = row("Outline Color:", colorInput(outlineHex, v => { outlineHex = v; updatePolygonPreview(tri); }));
            const r4 = row("Outline Width:", number(outlineWidth, 1, 24, 1, v => { outlineWidth = v; updatePolygonPreview(tri); }));
            const r5 = row("Outline Style:", styleSelect(outlineStyle, v => { outlineStyle = v; updatePolygonPreview(tri); }));

            // const noFillRow = document.createElement("div");
            // noFillRow.className = "chk";
            // noFillRow.innerHTML = `
            //   <input id="noFillChk" type="checkbox"/>
            //   <label for="noFillChk" class="small">No Fill (transparent)</label>
            // `;

            // const noFillChk = noFillRow.querySelector<HTMLInputElement>("#noFillChk")!;
            // noFillChk.checked = noFill;
            // noFillChk.onchange = () => {
            //     noFill = noFillChk.checked;
            //     fillColorInput.toggleAttribute("disabled", noFill);
            //     (fillRange.querySelector('input[type="range"]') as HTMLInputElement).toggleAttribute("disabled", noFill);
            //     updatePolygonPreview(tri);
            // };

            controlsHost.append(galLabel, gal, r1, r2, r3, r4, r5);

            fillColorInput.toggleAttribute("disabled", noFill);
            (fillRange.querySelector('input[type="range"]') as HTMLInputElement).toggleAttribute("disabled", noFill);
            updatePolygonPreview(tri);
        }


        if (family === "point") {
            const circ = document.createElementNS("http://www.w3.org/2000/svg", "circle");
            circ.setAttribute("cx", "50"); circ.setAttribute("cy", "25"); circ.setAttribute("r", String(pointSize / 3));
            previewSvg.appendChild(circ);

            const r0 = row("Symbol Size:", number(pointSize, 6, 64, 1, v => { pointSize = v; updatePointPreview(circ); }));
            const pointColorInput = colorInput(pointFillHex, v => {
                pointFillHex = v;
                updatePointPreview(circ);
            });
            const r1 = row("Color:", pointColorInput);
            const r2 = row("Transparency:", range(pointTransparencyPct, v => { pointTransparencyPct = v; updatePointPreview(circ); }, "%"));
            const r3 = row("Outline Color:", colorInput(pointOutlineHex, v => { pointOutlineHex = v; updatePointPreview(circ); }));
            const r4 = row("Outline Width:", number(pointOutlineWidth, 0, 12, 1, v => { pointOutlineWidth = v; updatePointPreview(circ); }));

            // tiny point gallery (a few presets)
            const galLabel = document.createElement("div"); galLabel.className = "small"; galLabel.textContent = "Basic";
            const gal = document.createElement("div"); gal.className = "gallery";
            const pts = ["#ef4444", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#111827"];
            let selIndex = 3;
            pts.forEach((hex, i) => {
                const sw = document.createElement("button"); sw.type = "button"; sw.className = "sw";
                sw.style.background = hex; sw.setAttribute("aria-selected", String(i === selIndex));
                sw.onclick = () => {
                    pointFillHex = hex;
                    pointColorInput.value = hex;
                    updatePointPreview(circ);
                    gal.querySelectorAll(".sw").forEach(n => n.setAttribute("aria-selected", "false"));
                    sw.setAttribute("aria-selected", "true");
                };
                gal.appendChild(sw);
            });

            controlsHost.append(galLabel, gal, r0, r1, r2, r3, r4);
            updatePointPreview(circ);
        }

        if (family === "text") {
            const t = document.createElementNS("http://www.w3.org/2000/svg", "text");
            t.setAttribute("x", "50"); t.setAttribute("y", "28");
            t.setAttribute("text-anchor", "middle");
            t.textContent = textValue || "Text";
            previewSvg.appendChild(t);

            const r0 = row("Text:", (() => {
                const i = document.createElement("input"); i.type = "text"; i.value = textValue;
                i.oninput = () => { textValue = i.value; t.textContent = textValue || "Text"; };
                return i;
            })());

            const r1 = row("Font Color:", colorInput(textColorHex, v => {
                textColorHex = v;
                const c = hexToRgba(v, 1);
                t.setAttribute("fill", `rgba(${c.r},${c.g},${c.b},${c.a})`);
            }));

            const r2 = row("Font Size:", number(textSize, 6, 96, 1, v => {
                textSize = v || 16;
                t.setAttribute("font-size", String(textSize));
            }));

            controlsHost.append(r0, r1, r2);
            const c = hexToRgba(textColorHex, 1);
            t.setAttribute("fill", `rgba(${c.r},${c.g},${c.b},${c.a})`);
            t.setAttribute("font-size", String(textSize));
        }

    }

    function row(labelText: string, control: HTMLElement) {
        const r = document.createElement("div"); r.className = "row";
        const l = document.createElement("label"); l.textContent = labelText;
        r.append(l, control);
        return r;
    }

    function colorInput(value: string, on: (hex: string) => void) {
        const i = document.createElement("input"); i.type = "color"; i.value = value; i.className = "stroke";
        i.oninput = () => on(i.value);
        return i;
    }

    function number(value: number, min: number, max: number, step: number, on: (v: number) => void) {
        const i = document.createElement("input"); i.type = "number"; i.value = String(value);
        i.min = String(min); i.max = String(max); i.step = String(step);
        i.oninput = () => on(Number(i.value || value));
        return i;
    }

    function range(value: number, on: (v: number) => void, suffix = "") {
        const box = document.createElement("div"); box.style.display = "flex"; box.style.gap = "8px"; box.style.alignItems = "center"; box.style.width = "100%";
        const i = document.createElement("input"); i.type = "range"; i.min = "0"; i.max = "100"; i.step = "1"; i.value = String(value);
        const s = document.createElement("span"); s.className = "small"; s.style.width = "36px"; s.style.textAlign = "right"; s.textContent = `${value}${suffix}`;
        i.oninput = () => { s.textContent = `${i.value}${suffix}`; on(Number(i.value)); };
        box.append(i, s);
        return box;
    }

    function styleSelect(value: LineStyle, on: (v: LineStyle) => void) {
        const sel = document.createElement("select");
        ["solid", "dash", "dot", "dash-dot", "long-dash", "long-dash-dot", "none"].forEach(v => {
            const o = document.createElement("option"); o.value = v; o.textContent = labelize(v);
            if (v === value) o.selected = true; sel.appendChild(o);
        });
        sel.onchange = () => on(sel.value as LineStyle);
        return sel;
    }

    function labelize(v: string) { return v.replace(/-/g, " ").replace(/\b\w/g, s => s.toUpperCase()); }

    function updateLinePreview(el: SVGLineElement) {
        const c = hexToRgba(strokeHex, 1 - strokeTransparencyPct / 100);
        el.setAttribute("stroke", `rgba(${c.r},${c.g},${c.b},${c.a})`);
        el.setAttribute("stroke-width", String(strokeWidth));
        const dash = svgDashFor(strokeStyle);
        if (dash) el.setAttribute("stroke-dasharray", dash); else el.removeAttribute("stroke-dasharray");
    }

    function updatePolygonPreview(el: SVGPolygonElement) {
        if (noFill) {
            el.setAttribute("fill", "none");
        } else {
            const f = hexToRgba(fillHex, 1 - fillTransparencyPct / 100);
            el.setAttribute("fill", `rgba(${f.r},${f.g},${f.b},${f.a})`);
        }
        const o = hexToRgba(outlineHex, 1);
        el.setAttribute("stroke", `rgba(${o.r},${o.g},${o.b},${o.a})`);
        el.setAttribute("stroke-width", String(outlineWidth));
        const dash = svgDashFor(outlineStyle);
        if (dash) el.setAttribute("stroke-dasharray", dash); else el.removeAttribute("stroke-dasharray");
    }

    function updatePointPreview(el: SVGCircleElement) {
        const f = hexToRgba(pointFillHex, 1 - pointTransparencyPct / 100);
        el.setAttribute("fill", `rgba(${f.r},${f.g},${f.b},${f.a})`);
        const o = hexToRgba(pointOutlineHex, 1);
        el.setAttribute("stroke", `rgba(${o.r},${o.g},${o.b},${o.a})`);
        el.setAttribute("stroke-width", String(pointOutlineWidth));
        el.setAttribute("r", String(Math.max(3, pointSize / 3)));
    }

    // ======= symbol builders =======
    function lineSymbol(): SimpleLineSymbol {
        return new SimpleLineSymbol({
            color: new Color([...rgbOf(strokeHex), 1 - strokeTransparencyPct / 100]),
            width: strokeWidth,
            style: STYLE_MAP[strokeStyle],
        });
    }

    function fillSymbol(): SimpleFillSymbol {
        const hasFill = !noFill;
        return new SimpleFillSymbol({
            style: hasFill ? "solid" as const : "none" as const,
            color: hasFill ? new Color([...rgbOf(fillHex), 1 - fillTransparencyPct / 100]) : null as any,
            outline: new SimpleLineSymbol({
                color: new Color([...rgbOf(outlineHex), 1]),
                width: outlineWidth,
                style: STYLE_MAP[outlineStyle],
            }),
        });
    }

    function pointSymbol(): SimpleMarkerSymbol {
        return new SimpleMarkerSymbol({
            color: new Color([...rgbOf(pointFillHex), 1 - pointTransparencyPct / 100]),
            size: pointSize,
            outline: new SimpleLineSymbol({
                color: new Color([...rgbOf(pointOutlineHex), 1]),
                width: pointOutlineWidth,
            }),
        });
    }

    // ======= draw plumbing =======
    const disableForMeasure = () => {
        const disabled = (view as any).__measuring === true;
        grid.querySelectorAll<HTMLButtonElement>(".btn[data-tool]").forEach(b => b.disabled = disabled);
    };
    disableForMeasure();
    const measWatch = view.watch("updating", () => disableForMeasure());

    function stopCurrent(keepSelection = false) {
        try { actionHandles.forEach(h => h.remove()); } catch { }
        actionHandles = [];
        try { activeAction?.destroy(); } catch { }
        activeAction = null;

        (view as any).__drawing = false;

        if (!keepSelection) {
            deselectButtons();
        }
        updateUndoRedoButtons();

        if (preview) { try { sketchLayer.remove(preview); } catch { } }
        preview = null;

        try { sketchLayer.removeAll(); } catch { }
    }

    function deselectButtons() {
        grid.querySelectorAll<HTMLButtonElement>(".btn[data-tool]")
            .forEach(b => b.setAttribute("aria-pressed", "false"));
        selectedBtn = null;
    }

    function commit(geometry: __esri.Geometry) {
        const sym = (activeType === "text")
            ? textSymbol()
            : geometry.type === "point" ? pointSymbol()
                : geometry.type === "polyline" ? lineSymbol()
                    : fillSymbol();

        const batch: Graphic[] = [];
        const g = new Graphic({ geometry, symbol: sym });
        layer.add(g);
        batch.push(g);

        if (measChk.checked && geometry.type !== "point" && activeType !== "text") {
            const meas = buildMeasurementGraphics(g);
            if (meas.length) { layer.addMany(meas); batch.push(...meas); }
        }

        doneStack.push(batch);
        redoStack.length = 0;
        updateUndoRedoButtons();
        opts.onComplete?.(g);
    }

    function updatePreviewGraphic(geom: __esri.Geometry) {
        const sym = (activeType === "text")
            ? textSymbol()
            : geom.type === "point" ? pointSymbol()
                : geom.type === "polyline" ? lineSymbol()
                    : fillSymbol();

        if (!preview) {
            preview = new Graphic({ geometry: geom, symbol: sym });
            sketchLayer.add(preview);
        } else {
            preview.geometry = geom as any;
            preview.symbol = sym as any;
        }
    }

    // helpers to build shapes for segment actions
    function toPointXY(x: number, y: number, sr = view.spatialReference) { return new Point({ x, y, spatialReference: sr }); }
    function twoPointsFromVertices(vertices: any): [Point, Point] | null {
        if (!vertices) return null;
        if (Array.isArray(vertices[0]) && Array.isArray(vertices[0][0])) {
            const a = vertices[0][0], b = vertices[0][1];
            if (a && b) return [toPointXY(a[0], a[1]), toPointXY(b[0], b[1])];
        } else if (Array.isArray(vertices) && vertices.length >= 2) {
            const a = vertices[0], b = vertices[1];
            return [toPointXY(a[0], a[1]), toPointXY(b[0], b[1])];
        }
        return null;
    }
    function rectangleFromTwo(p1: Point, p2: Point): Polygon {
        const xmin = Math.min(p1.x, p2.x), xmax = Math.max(p1.x, p2.x);
        const ymin = Math.min(p1.y, p2.y), ymax = Math.max(p1.y, p2.y);
        return Polygon.fromExtent(new Extent({ xmin, ymin, xmax, ymax, spatialReference: view.spatialReference }));
    }
    function circleFromTwo(center: Point, edge: Point): Circle {
        const radiusM = geometryEngine.geodesicLength(
            new Polyline({ spatialReference: view.spatialReference, paths: [[[center.x, center.y], [edge.x, edge.y]]] }),
            "meters"
        );
        return new Circle({ center, radius: Math.max(0, radiusM), radiusUnit: "meters", geodesic: true, numberOfPoints: 120 });
    }
    function ellipseFromTwo(p1: Point, p2: Point): Polygon {
        const cx = (p1.x + p2.x) / 2, cy = (p1.y + p2.y) / 2;
        const rx = Math.abs(p2.x - p1.x) / 2, ry = Math.abs(p2.y - p1.y) / 2;
        const N = 120, ring: number[][] = [];
        for (let i = 0; i <= N; i++) { const t = (i / N) * Math.PI * 2; ring.push([cx + rx * Math.cos(t), cy + ry * Math.sin(t)]); }
        return new Polygon({ rings: [ring], spatialReference: view.spatialReference });
    }

    const commitFromEvent = (evt: any, type: DrawType): __esri.Geometry | undefined => {
        const t: DrawType = (type === "text") ? "point" : type;

        try {
            if (t === "point") {
                const coords = evt?.coordinates ?? (evt?.vertices?.[0] ?? null);
                if (!coords) return;
                const [x, y] = Array.isArray(coords[0]) ? coords[0] : coords;
                return new Point({ x, y, spatialReference: view.spatialReference });
            }
            if (t === "polyline") {
                return new Polyline({ paths: evt.vertices, spatialReference: view.spatialReference });
            }
            if (t === "polygon") {
                return new Polygon({ rings: evt.vertices, spatialReference: view.spatialReference });
            }
            const pair = twoPointsFromVertices(evt.vertices); if (!pair) return;
            const [a, b] = pair;
            if (t === "rectangle") return rectangleFromTwo(a, b);
            if (t === "circle") return circleFromTwo(a, b);
            if (t === "ellipse") return ellipseFromTwo(a, b);
        } catch { }
    };

    function start(type: DrawType, explicitMode?: DrawMode) {
        stopCurrent(true);

        (view as any).__drawing = true;
        if ((view as any).__measuring) return;

        activeType = type;
        family = (type === "point") ? "point"
            : (type === "polyline") ? "line"
                : (type === "text") ? "text"
                    : "polygon";
        activeMode = explicitMode;

        showPanel(true);
        renderControls();

        let mode: DrawMode | undefined = explicitMode;
        if (!mode) {
            if (type === "polyline" || type === "polygon" || type === "text") mode = "click";
            if (type === "rectangle" || type === "circle" || type === "ellipse") mode = "click";
        }
        const action = draw.create(type === "text" ? "point" : type, mode ? { mode } : undefined);
        activeAction = action;

        updateUndoRedoButtons();

        const onUpdate = (evt: any) => {
            const geom = commitFromEvent(evt, type === "text" ? "point" : type);
            if (geom) updatePreviewGraphic(geom);
            updateUndoRedoButtons();
        };

        const onComplete = (evt: any) => {
            const geom = commitFromEvent(evt, type === "text" ? "point" : type);
            stopCurrent(true);
            if (geom) commit(geom);
            deselectButtons();
        };

        actionHandles.push(
            action.on("cursor-update", onUpdate),
            action.on("vertex-add", onUpdate),
            action.on("vertex-remove", onUpdate),
            action.on("redo", onUpdate),
            action.on("undo", onUpdate),
            action.on("draw-complete", onComplete)
        );
    }


    // Button wiring
    grid.querySelectorAll<HTMLButtonElement>(".btn[data-tool]").forEach(btn => {
        btn.onclick = () => {
            deselectButtons();
            btn.setAttribute("aria-pressed", "true");
            selectedBtn = btn;

            const tool = btn.dataset.tool as DrawType;
            const m = btn.dataset.mode as DrawMode | undefined;
            start(tool, m);
        };
    });

    // Footer
    const undoBtn = footer.querySelector<HTMLButtonElement>("#undoBtn")!;
    const redoBtn = footer.querySelector<HTMLButtonElement>("#redoBtn")!;
    const clearBtn = footer.querySelector<HTMLButtonElement>("#clearBtn")!;

    function updateUndoRedoButtons() {
        undoBtn.disabled = doneStack.length === 0;
        redoBtn.disabled = redoStack.length === 0;
    }
    updateUndoRedoButtons();

    undoBtn.onclick = () => {
        const op = doneStack.pop();
        if (!op) return;
        try { layer.removeMany(op); } catch { op.forEach(g => { try { layer.remove(g); } catch { } }); }
        redoStack.push(op);
        updateUndoRedoButtons();
    };

    redoBtn.onclick = () => {
        const op = redoStack.pop();
        if (!op) return;
        try { layer.addMany(op); } catch { op.forEach(g => { try { layer.add(g); } catch { } }); }
        doneStack.push(op);
        updateUndoRedoButtons();
    };

    clearBtn.onclick = () => {
        stopCurrent();
        layer.removeAll();
        doneStack = [];
        redoStack = [];
        updateUndoRedoButtons();
    };

    // initial default
    renderControls();

    function destroy() {
        stopCurrent();
        try { view.map?.remove(layer); } catch { }
        try { view.map?.remove(sketchLayer); } catch { }
        try { root.remove(); } catch { }
        try { measWatch.remove(); } catch { }
    }


    function setVisible(v: boolean) {
        root.style.display = v ? "block" : "none";
        if (!v) stopCurrent();
    }

    return {
        element: root,
        getLayer: () => layer,
        clear: () => layer.removeAll(),
        destroy,
        setVisible,
        start,
    };
}

/* ===== helpers ===== */
function rgbOf(hex: string): [number, number, number] {
    const h = hex.replace("#", "");
    const n = parseInt(h.length === 3 ? h.split("").map(x => x + x).join("") : h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function toHex(c: string | number[]): string {
    const [r, g, b] = Array.isArray(c) ? (c.length === 4 ? [c[0], c[1], c[2]] : c as any) : [0, 121, 193];
    const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v <= 1 ? v * 255 : v)));
    return `#${[r, g, b].map(x => clamp(x).toString(16).padStart(2, "0")).join("")}`;
}
function hexToRgba(hex: string, a = 1) { const [r, g, b] = rgbOf(hex); return { r, g, b, a }; }
function svgDashFor(style: LineStyle): string | null {
    switch (style) {
        case "solid": return null;
        case "dash": return "8 6";
        case "dot": return "2 6";
        case "dash-dot": return "10 6 2 6";
        case "long-dash": return "14 6";
        case "long-dash-dot": return "14 6 2 6";
        case "none": return "0 1000";
        default: return null;
    }
}
function fmtFeet(v: number) { return Number.isFinite(v) ? `${v.toFixed(2)} ft` : ""; }
function midPointOnLine(line: Polyline): Point {
    const path = line.paths[0];
    let total = 0;
    for (let i = 1; i < path.length; i++) total += dist(path[i - 1], path[i]);
    const target = total / 2;
    let acc = 0;
    for (let i = 1; i < path.length; i++) {
        const seg = dist(path[i - 1], path[i]);
        if (acc + seg >= target) {
            const t = (target - acc) / seg;
            const x = path[i - 1][0] + t * (path[i][0] - path[i - 1][0]);
            const y = path[i - 1][1] + t * (path[i][1] - path[i - 1][1]);
            return new Point({ x, y, spatialReference: line.spatialReference });
        }
        acc += seg;
    }
    const last = path[path.length - 1];
    return new Point({ x: last[0], y: last[1], spatialReference: line.spatialReference });
}
function dist(a: number[], b: number[]) { const dx = b[0] - a[0], dy = b[1] - a[1]; return Math.hypot(dx, dy); }

function buildMeasurementGraphics(g: Graphic): Graphic[] {
    const out: Graphic[] = [];
    try {
        if (g.geometry?.type === "polyline") {
            const len = geometryEngine.geodesicLength(g.geometry as Polyline, "feet");
            const mid = midPointOnLine(g.geometry as Polyline);
            const t = new TextSymbol({ text: fmtFeet(len), haloColor: "#fff", haloSize: 1, font: { size: 10 } });
            out.push(new Graphic({ geometry: mid, symbol: t }));
        } else if (g.geometry?.type === "polygon") {
            const poly = g.geometry as Polygon;
            const area = geometryEngine.geodesicArea(poly, "acres");
            let c: Point | null = (poly as any).centroid as Point | null;
            if (!c) c = poly.extent?.center as Point;
            if (c) {
                const t = new TextSymbol({ text: `${area.toFixed(2)} ac`, haloColor: "#fff", haloSize: 1, font: { size: 10 } });
                out.push(new Graphic({ geometry: c, symbol: t }));
            }
        }
    } catch { }
    return out;
}

function textSymbol(): TextSymbol {
    const c = hexToRgba(textColorHex, 1);
    const h = hexToRgba(textHaloHex, 1);
    return new TextSymbol({
        text: textValue || "Text",
        color: new Color([c.r, c.g, c.b, c.a]),
        haloColor: new Color([h.r, h.g, h.b, h.a]),
        haloSize: textHaloSize,
        font: { size: textSize }
    });
}